/**
 * @license
 * Copyright (c) 2021-present, FriendliAI Inc. All rights reserved.
 */

import React from 'react';
import { type RouteObject } from 'react-router-dom';

import { resetAllIntegrations } from '@fai/friendli-suite/features/auth/reset-all-integrations';
import { updateIntercomUser } from '@fai/friendli-suite/features/routes/loaders/intercom';
import { setSentryUser } from '@fai/friendli-suite/features/routes/loaders/sentry';
import {
  redirectIfLoggedInUserIsInvited,
  strictCheckIfErrorCodesExistsInQueryString,
  strictFetchInvitationDetails,
} from '@fai/friendli-suite/features/routes/loaders/user';
import AuthLayout from '@fai/friendli-suite/views/layout/auth/auth-layout';
import {
  docsHeaderLinkProps,
  pricingHeaderLinkProps,
  websiteHeaderLinkProps,
} from '@fai/friendli-suite/views/layout/auth/links';
import { pipeLoaders } from '@fai/third-party/react-router-dom';
import { redirectIfSessionExpired } from '@fai/third-party/supertokens';

const AuthOnboardingInvitePage = React.lazy(
  () => import('@fai/friendli-suite/pages/auth/onboarding/invite')
);
const AuthInvitePage = React.lazy(() => import('@fai/friendli-suite/pages/auth/invite'));
const AuthLoginInvitePage = React.lazy(() => import('@fai/friendli-suite/pages/auth/login/invite'));
const AuthSignupInvitePage = React.lazy(
  () => import('@fai/friendli-suite/pages/auth/signup/invite')
);

export const invitationRoutes: RouteObject[] = [
  // SESSION REQUIRED
  {
    loader: pipeLoaders({
      sequence: [redirectIfSessionExpired],
      concurrent: [setSentryUser, updateIntercomUser],
    }),
    Component: AuthLayout,
    children: [
      {
        path: 'invite',
        loader: pipeLoaders({
          sequence: [strictCheckIfErrorCodesExistsInQueryString, strictFetchInvitationDetails],
        }),
        Component: AuthInvitePage,
      },
      {
        path: 'onboarding/invite',
        Component: AuthOnboardingInvitePage,
      },
    ],
  },

  // WITH/WITHOUT SESSION
  {
    loader: strictCheckIfErrorCodesExistsInQueryString,
    Component: () => (
      <AuthLayout
        rightItems={[docsHeaderLinkProps, pricingHeaderLinkProps, websiteHeaderLinkProps]}
      />
    ),
    children: [
      {
        path: 'signup/invite',
        loader: pipeLoaders({
          sequence: [redirectIfLoggedInUserIsInvited('/invite'), resetAllIntegrations],
        }),
        Component: AuthSignupInvitePage,
      },
      {
        path: 'login/invite',
        loader: pipeLoaders({
          sequence: [redirectIfLoggedInUserIsInvited('/invite'), resetAllIntegrations],
        }),
        Component: AuthLoginInvitePage,
      },
    ],
  },
];
